import { useId, useState } from 'react'
import type { FormEvent, ReactElement } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router'
import { readRedirectTarget } from '../components/protected-route'
import { authErrorMessageKey } from '../lib/auth-client'
import type { AuthFailure } from '../lib/auth-client'
import { useTranslate } from '../lib/i18n-provider'
import { useSession } from '../lib/session-context'

/**
 * Tweede factor: `POST /auth/mfa/verify`.
 *
 * Twee invoervormen, één veld in het contract. Normaal komt hier de zescijferige
 * TOTP-code uit de authenticator; is de telefoon kwijt, dan mag een van de tien
 * herstelcodes in de plaats. Het contract kent maar één veld (`code`), dus beide
 * gaan daar doorheen en de API herkent ze aan hun vorm. Een tweede veld verzinnen
 * zou van het contract afwijken.
 *
 * Waarom de herstelcode een aparte modus is en niet gewoon "typ maar wat in het
 * codeveld": het TOTP-veld hoort `inputmode="numeric"`, `maxlength=6` en
 * `autocomplete="one-time-code"` te hebben, en met die drie eigenschappen is een
 * herstelcode niet in te voeren. Eén veld dat beide moet kunnen, kan geen van
 * beide goed.
 */

type CodeMode = 'totp' | 'recovery'

/** Lengte van een TOTP-code: 6 cijfers, SHA-1, 30 seconden (§17.11). */
const TOTP_CODE_LENGTH = 6

export function MfaPage(): ReactElement {
  const t = useTranslate()
  const navigate = useNavigate()
  const location = useLocation()
  const { state, mfaPending, verifyMfa } = useSession()

  const codeId = useId()

  const [mode, setMode] = useState<CodeMode>('totp')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<AuthFailure | null>(null)

  const target = readRedirectTarget(location.state)

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    setBusy(true)
    setFailure(null)

    const result = await verifyMfa(code.trim())
    setBusy(false)

    if (!result.ok) {
      setFailure(result.error)
      // De code is eenmalig; hem laten staan nodigt uit tot nogmaals proberen met
      // dezelfde waarde, wat per definitie weer mislukt.
      setCode('')
      return
    }
    void navigate(target, { replace: true })
  }

  function switchMode(next: CodeMode): void {
    setMode(next)
    setCode('')
    setFailure(null)
  }

  if (state.phase === 'loading') {
    return (
      <p className="page__status" role="status">
        {t('common.state.loading')}
      </p>
    )
  }

  if (state.phase === 'authenticated') {
    return <Navigate to={target} replace />
  }

  // Zonder lopende inlogpoging kan verifiëren niet slagen: de API heeft geen
  // sessie met mfa_pending om aan te koppelen. `GET /auth/session` geeft voor zo'n
  // sessie 401, dus dit valt niet uit de server te lezen — bij een herlaadde
  // pagina is de poging weg en begint het inloggen opnieuw. Dat kost één extra
  // handeling, en is beter dan een formulier dat nooit iets kan opleveren.
  if (!mfaPending) {
    return <Navigate to="/login" replace state={location.state} />
  }

  const isRecovery = mode === 'recovery'

  return (
    <section className="page" aria-labelledby="mfa-heading">
      <h1 className="page__title" id="mfa-heading">
        {t('dashboard.auth.mfa.title')}
      </h1>

      <p className="page__status">
        {isRecovery ? t('dashboard.auth.mfa.recoveryHint') : t('dashboard.auth.mfa.hint')}
      </p>

      {failure !== null && (
        <div className="notice notice--error" role="alert">
          <p className="notice__title">{t(authErrorMessageKey(failure, 'mfa'))}</p>
          {/* Technisch detail, bewust onvertaald. */}
          <code className="notice__detail">{failure.message}</code>
        </div>
      )}

      <form
        className="auth-form"
        onSubmit={(event) => {
          void handleSubmit(event)
        }}
      >
        <div className="auth-form__field">
          <label className="auth-form__label" htmlFor={codeId}>
            {isRecovery ? t('dashboard.auth.mfa.recoveryCode') : t('dashboard.auth.mfa.code')}
          </label>
          {isRecovery ? (
            <input
              autoComplete="off"
              className="auth-form__input"
              disabled={busy}
              id={codeId}
              name="recoveryCode"
              onChange={(event) => {
                setCode(event.target.value)
              }}
              required
              spellCheck={false}
              type="text"
              value={code}
            />
          ) : (
            <input
              autoComplete="one-time-code"
              className="auth-form__input"
              disabled={busy}
              id={codeId}
              inputMode="numeric"
              maxLength={TOTP_CODE_LENGTH}
              name="code"
              onChange={(event) => {
                // Alleen cijfers: plakken uit een authenticator levert soms een
                // spatie of een smalle spatie mee.
                setCode(event.target.value.replace(/\D/g, '').slice(0, TOTP_CODE_LENGTH))
              }}
              pattern={`\\d{${String(TOTP_CODE_LENGTH)}}`}
              required
              type="text"
              value={code}
            />
          )}
        </div>

        <div className="auth-form__actions">
          <button className="button" disabled={busy || code.trim() === ''} type="submit">
            {busy ? t('dashboard.auth.mfa.submitting') : t('dashboard.auth.mfa.submit')}
          </button>
          <button
            className="button"
            disabled={busy}
            onClick={() => {
              switchMode(isRecovery ? 'totp' : 'recovery')
            }}
            type="button"
          >
            {isRecovery ? t('dashboard.auth.mfa.useCode') : t('dashboard.auth.mfa.useRecovery')}
          </button>
        </div>
      </form>
    </section>
  )
}
